
import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ActiveDirectoryConfig } from '../../system/types';

interface ConnectionFormProps {
  adConfig: ActiveDirectoryConfig;
  autoSync: boolean;
  setAutoSync: (value: boolean) => void;
  handleConfigChange: (field: keyof ActiveDirectoryConfig, value: string) => void;
  editing: boolean;
}

const ConnectionForm: React.FC<ConnectionFormProps> = ({
  adConfig,
  autoSync,
  setAutoSync,
  handleConfigChange,
  editing
}) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="ad-name">Connection Name</Label>
          <Input 
            id="ad-name" 
            value={adConfig.name} 
            onChange={(e) => handleConfigChange('name', e.target.value)} 
            disabled={!editing || adConfig.status === 'connected'} 
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ad-domain">Domain</Label>
          <Input 
            id="ad-domain" 
            value={adConfig.domain} 
            onChange={(e) => handleConfigChange('domain', e.target.value)} 
            disabled={!editing || adConfig.status === 'connected'} 
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ad-server">Server Address</Label>
          <Input 
            id="ad-server" 
            value={adConfig.server} 
            onChange={(e) => handleConfigChange('server', e.target.value)} 
            disabled={!editing || adConfig.status === 'connected'} 
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ad-port">Port</Label>
          <Input 
            id="ad-port" 
            value={adConfig.port} 
            onChange={(e) => handleConfigChange('port', e.target.value)} 
            disabled={!editing || adConfig.status === 'connected'} 
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ad-basedn">Base DN</Label>
          <Input 
            id="ad-basedn" 
            value={adConfig.baseDN} 
            onChange={(e) => handleConfigChange('baseDN', e.target.value)} 
            disabled={!editing || adConfig.status === 'connected'} 
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ad-username">Service Account Username</Label>
          <Input 
            id="ad-username" 
            value={adConfig.username} 
            onChange={(e) => handleConfigChange('username', e.target.value)} 
            disabled={!editing || adConfig.status === 'connected'} 
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ad-password">Service Account Password</Label>
          <Input 
            id="ad-password" 
            type="password" 
            placeholder={adConfig.status === 'connected' ? '••••••••' : 'Enter password'} 
            disabled={!editing || adConfig.status === 'connected'} 
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ad-sync">Sync Schedule</Label>
          <Select 
            disabled={!editing || !autoSync} 
            value={adConfig.syncSchedule}
            onValueChange={(value) => handleConfigChange('syncSchedule', value)}
          >
            <SelectTrigger id="ad-sync">
              <SelectValue placeholder="Select schedule" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Manual Only</SelectItem>
              <SelectItem value="hourly">Hourly</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center space-x-2 pt-4">
        <Switch 
          id="auto-sync" 
          checked={autoSync} 
          onCheckedChange={setAutoSync} 
          disabled={!editing || adConfig.status !== 'connected'} 
        />
        <Label htmlFor="auto-sync">Enable automatic synchronization</Label>
      </div>

      {adConfig.lastSync && (
        <p className="text-sm text-muted-foreground mt-2">
          Last synchronized: {new Date(adConfig.lastSync).toLocaleString()}
        </p>
      )}
    </div>
  );
};

export default ConnectionForm;
